/**
 * Board templates (`specs/M1-B9-export-templates.md` §6).
 *
 * A template is a **frozen snapshot** of a board's shape, not a live link. monday's own
 * *managed* templates are live, and they are the paid tier the Save dialog advertises with a 💎
 * (`discovery/ui-reference/templates/INDEX.md` §2); this product does not build them.
 */
import { z } from 'zod';
/** monday's dialog: `Public` (default) or `Private`. */
export const boardTemplateKinds = ['public', 'private'];
export const templateColumnSchema = z.object({
    id: z.string(),
    title: z.string(),
    type: z.string(),
    width: z.number().int().nullable().optional(),
    settings: z.record(z.string(), z.unknown()).default({}),
});
export const templateGroupSchema = z.object({
    id: z.string(),
    title: z.string(),
    color: z.string().nullable().optional(),
});
export const templateViewSchema = z.object({
    name: z.string(),
    kind: z.string(),
    settings: z.record(z.string(), z.unknown()).default({}),
});
export const templateItemSchema = z.object({
    name: z.string(),
    groupId: z.string(),
    /** Column id → the stored cell value, exactly as `cell_values.value` holds it. */
    cells: z.record(z.string(), z.unknown()).default({}),
});
export const boardTemplateDefinitionSchema = z.object({
    /** Bumped whenever the shape changes; a template of an older version still replays. */
    version: z.literal(1).default(1),
    board: z.object({
        name: z.string(),
        description: z.string().nullable().default(null),
        kind: z.string().default('public'),
        itemTerminology: z.string().nullable().default(null),
        itemTerminologyPlural: z.string().nullable().default(null),
    }),
    columns: z.array(templateColumnSchema).default([]),
    groups: z.array(templateGroupSchema).default([]),
    views: z.array(templateViewSchema).default([]),
    items: z.array(templateItemSchema).default([]),
    /**
     * **P-307.** The source board's *subitems* board, structure only.
     *
     * A template must never carry the `subtasks` column itself: its `settings.boardIds` names the
     * **source's** subitems board, so replaying it linked every new board to the original's
     * subitems — which is how a subitem Nico created on his own board landed on MK-Store-Updates'.
     * The link is therefore dropped at capture and what is kept is the subitems board's *shape*,
     * which `createBoardFromTemplate` replays onto a subitems board of the copy's own.
     *
     * `null` on a board that has never had a subitem, and on every template written before this.
     */
    subitemsBoard: z
        .object({ columns: z.array(templateColumnSchema).default([]) })
        .nullable()
        .default(null),
});
/**
 * monday prefills the name field with `<board name> template`
 * (`discovery/ui-reference/templates/INDEX.md` §2), clipped to the column's 255.
 */
export function defaultTemplateName(boardName) {
    return `${boardName} template`.slice(0, 255);
}
/**
 * `Save as a template` is greyed out on a private or shareable board, with the tooltip
 * *"This action is only available for main boards"*. `public` is monday's stored value for
 * "Main" (`[fl]`, M1-B1), so this is the whole rule.
 */
export const MAIN_BOARDS_ONLY_MESSAGE = 'This action is only available for main boards';
export function boardCanBeTemplate(kind) {
    return (kind ?? 'public') === 'public';
}
//# sourceMappingURL=index.js.map